/**
 * Reporting View — Manager view of direct reports' time entries.
 *
 * Uses the same HeaderBar as the attorney tracked/exported page.
 * Top-left: team member selector. Top-right: Tracked/Exported/Calendar tab pills.
 * Bottom row: same action buttons, date picker, add, export.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { ChevronsUpDown, Check, User, Timer, Send, Calendar, X, Building2, FolderOpen } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/shared/components/ui/command";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { cn } from "@/shared/lib/utils";
import { useAuth } from "@workos-inc/authkit-react";
import { HeaderBar } from "../components/HeaderBar";
import { useDateParams } from "@/shared/hooks/useDateParams";
import { useApplicationFilter } from "../hooks/useApplicationFilter";
import { useReportsCache, useReportingEntriesCache, useMattersCache, useTasksCache, useActivitiesCache, useAbbreviationsCache, useRestrictedWords, type ReportingEntry, type ReportingEntriesData } from "@/shared/cache";
import { useCacheMutations } from "@/shared/cache";
import { useReportingApi } from "../hooks/useReportingApi";
import { useExportEntries } from "../hooks/useExportEntries";
import { usePersistedState } from "../hooks/usePersistedState";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { toast } from "sonner";
import { TOAST_DURATION } from "@/shared/utils/toast";
import { logger } from "@/shared/utils/logger";
import AddEntryV2, { type AddEntryV2Handle } from "../components/v2/AddEntryV2";
import EditEntryV2, { type SelectedRowData } from "../components/v2/EditEntryV2";
import { CombineDialog } from "../components/CombineDialog";
import {
  ReportingTrackedTable,
  ReportingExportedTable,
  type Entry,
  type SortingMode,
  type SortDirection,
} from "../components/ReportingTable/ReportingTable";
import calendarStyles from "./CalendarView/CalendarView.module.css";
import navArrowLeftIcon from "@/assets/icons/navArrowLeft.svg";
import navArrowLeftWhiteIcon from "@/assets/icons/navArrowLeftWhite.svg";
import navArrowRightIcon from "@/assets/icons/navArrowRight.svg";
import navArrowRightWhiteIcon from "@/assets/icons/navArrowRightWhite.svg";

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

type ReportingTab = "tracked" | "exported" | "calendar";

function formatDateForApi(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Format ISO date to MM/dd/yyyy (same as EntriesContext.transformData) */
function formatDateForEntry(isoDate: string): string {
  const datePart = isoDate.split("T")[0];
  const [year, month, day] = datePart.split("-").map(Number);
  const m = String(month).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${m}/${d}/${year}`;
}

function toEntry(tx: ReportingEntry): Entry {
  return {
    id: tx.id,
    date: formatDateForEntry(tx.date),
    matter: tx.matter_number || "",
    matter_id: tx.matter_id || undefined,
    matter_name: tx.matter_description || undefined,
    description: tx.description || "",
    task: tx.task || "",
    activity: tx.activity || undefined,
    document_name: tx.document_name || undefined,
    duration: tx.units,
    end_time: tx.end_time || undefined,
    application: tx.application || undefined,
    status: tx.exported ? "exported" : "tracked",
  };
}

function hourLabelTo24Hour(label: string): number {
  if (label === "Unknown") return 99;
  const match = label.match(/^(\d+):00 (AM|PM)$/);
  if (!match) return 99;
  const hour = parseInt(match[1], 10);
  const isPM = match[2] === "PM";
  if (hour === 12) return isPM ? 12 : 0;
  return isPM ? hour + 12 : hour;
}

function groupEntriesByHour(entries: Entry[], direction: SortDirection = "desc"): Record<string, Entry[]> {
  const groups: Record<string, Entry[]> = {};
  entries.forEach((entry) => {
    let hourLabel = "Unknown";
    if (entry.end_time) {
      const parts = entry.end_time.split(":");
      if (parts.length >= 1) {
        const hours = parseInt(parts[0], 10);
        if (!isNaN(hours)) {
          const period = hours >= 12 ? "PM" : "AM";
          const displayHour = hours % 12 || 12;
          hourLabel = `${displayHour}:00 ${period}`;
        }
      }
    }
    if (!groups[hourLabel]) groups[hourLabel] = [];
    groups[hourLabel].push(entry);
  });

  const mult = direction === "asc" ? 1 : -1;
  const orderedGroups: Record<string, Entry[]> = {};
  Object.keys(groups)
    .sort((a, b) => mult * (hourLabelTo24Hour(a) - hourLabelTo24Hour(b)))
    .forEach((key) => { orderedGroups[key] = groups[key]; });
  return orderedGroups;
}

function groupEntriesByMatter(entries: Entry[], direction: SortDirection = "asc"): Record<string, Entry[]> {
  const groups: Record<string, Entry[]> = {};
  entries.forEach((entry) => {
    const key = entry.matter_name || entry.matter || "UNKNOWN";
    if (!groups[key]) groups[key] = [];
    groups[key].push(entry);
  });

  const mult = direction === "asc" ? 1 : -1;
  const sorted: Record<string, Entry[]> = {};
  Object.keys(groups)
    .sort((a, b) => mult * a.localeCompare(b))
    .forEach((key) => { sorted[key] = groups[key]; });
  return sorted;
}


// ─────────────────────────────────────────────
// Reporting Member Selector (rendered as HeaderBar leftContent)
// ─────────────────────────────────────────────

function ReportingMemberSelector({
  reports,
  selectedProfId,
  onSelect,
}: {
  reports: { manages: Array<{ prof_id: string; full_name: string; email: string; prof_type: string | null }> };
  selectedProfId: string | null;
  onSelect: (profId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedMember = reports.manages.find((p) => p.prof_id === selectedProfId);

  const filtered = useMemo(() => {
    if (!search) return reports.manages;
    const term = search.toLowerCase();
    return reports.manages.filter(
      (p) =>
        p.full_name.toLowerCase().includes(term) ||
        p.email.toLowerCase().includes(term) ||
        (p.prof_type && p.prof_type.toLowerCase().includes(term))
    );
  }, [reports.manages, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-muted transition-colors text-left">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-foreground text-sm font-medium">
            {selectedMember ? (
              selectedMember.full_name
                .split(/[\s,]+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((n) => n[0])
                .join("")
                .toUpperCase()
            ) : (
              <User className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium text-foreground truncate">
              {selectedMember ? selectedMember.full_name : "Select team member"}
            </span>
            {selectedMember?.prof_type && (
              <span className="text-xs text-muted-foreground truncate">{selectedMember.prof_type}</span>
            )}
            {!selectedMember && (
              <span className="text-xs text-muted-foreground">Click to choose</span>
            )}
          </div>
          <ChevronsUpDown className="ml-1 h-4 w-4 text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search by name, email, or role..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>No team members found.</CommandEmpty>
            <CommandGroup>
              {filtered.map((member) => (
                <CommandItem
                  key={member.prof_id}
                  value={`${member.full_name} ${member.email} ${member.prof_type || ""}`}
                  onSelect={() => { onSelect(member.prof_id); setOpen(false); setSearch(""); }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", selectedProfId === member.prof_id ? "opacity-100" : "opacity-0")} />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{member.full_name}</span>
                    <span className="text-xs text-muted-foreground truncate">
                      {member.email}{member.prof_type && ` · ${member.prof_type}`}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

export default function ReportingView() {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const { reports, isLoading: reportsLoading } = useReportsCache();

  // URL-driven state
  const [searchParams, setSearchParams] = useSearchParams();
  const { date: selectedDate, dateRange, setDate: setSelectedDate, setDateRange } = useDateParams();

  // Tab from URL (?view=tracked|exported|calendar)
  const activeTab = useMemo<ReportingTab>(() => {
    const v = searchParams.get("view");
    if (v === "tracked" || v === "calendar") return v;
    return "exported";
  }, [searchParams]);

  const setActiveTab = useCallback((tab: ReportingTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === "exported") next.delete("view");
      else next.set("view", tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Derive date string for API
  const selectedDateStr = useMemo(() => formatDateForApi(selectedDate), [selectedDate]);

  // URL-driven filters
  const clientFilter = searchParams.get("client") || null;
  const matterFilter = searchParams.get("matter") || null;

  const setParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (!value) next.delete(key);
        else next.set(key, value);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Selected attorney from URL (?attorney=PROF_ID)
  const selectedProfId = searchParams.get("attorney") || null;
  const setSelectedProfId = useCallback((profId: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (profId) next.set("attorney", profId);
      else next.delete("attorney");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Persisted state (non-URL)
  const [sortingMode, setSortingMode] = usePersistedState<SortingMode>("reporting:sortingMode", "time");
  const [sortDirection, setSortDirection] = usePersistedState<SortDirection>("reporting:sortDirection", "desc");

  // Application filter
  const {
    isVisible: isApplicationVisible,
    toggleApplication,
    filterEntries,
    isFilterActive,
    showAll: showAllApplications,
    hideAll: hideAllApplications,
  } = useApplicationFilter(user?.email || "");

  // Calendar state
  const [calendarMonthDate, setCalendarMonthDate] = useState<Date>(new Date());

  // Cached team entries (all dates, all team members — filters client-side)
  const { tracked: cachedTracked, exported: cachedExported, isLoading: entriesLoading } = useReportingEntriesCache(selectedProfId);
  const { refresh, optimisticUpdate } = useCacheMutations();

  // Reporting API (injects x-act-as-prof-id header)
  const { makeReportingRequest } = useReportingApi(selectedProfId);

  // Mutation tracking (visual disabled state on entries being modified)
  const [mutatingIds, setMutatingIds] = useState<Set<number>>(new Set());

  // Failed export tracking
  const [failedExportEntries, setFailedExportEntries] = useState<Map<number, string>>(new Map());

  // Export hook
  const { exportEntries, exportingIds, isExporting } = useExportEntries({
    makeApiRequest: makeReportingRequest,
    onComplete: ({ failedEntries }) => {
      refresh('reporting_entries');
      if (failedEntries.length > 0) {
        setFailedExportEntries((prev) => {
          const next = new Map(prev);
          for (const f of failedEntries) next.set(f.id, f.error);
          return next;
        });
      }
    },
    progressToastId: "reporting-export-progress",
  });

  // Lookup data for Add/Edit/Combine dialogs
  const { matters } = useMattersCache();
  const { tasks } = useTasksCache();
  const { activities } = useActivitiesCache();
  const { abbreviationProps } = useAbbreviationsCache();
  const { getRestrictedWords } = useRestrictedWords();

  // Add entry dialog
  const addDialogRef = useRef<AddEntryV2Handle>(null);

  // Edit entry dialog
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<SelectedRowData | null>(null);

  // Combine dialog
  const [combineDialogOpen, setCombineDialogOpen] = useState(false);

  // Selection state
  const [checkedRows, setCheckedRows] = useState<Set<number>>(new Set());
  const [actionInProgress, setActionInProgress] = useState<"combine" | "delete" | "duplicate" | null>(null);

  // Clear selection when switching tabs or team members
  const prevProfId = useRef(selectedProfId);
  const prevTab = useRef(activeTab);
  useEffect(() => {
    if (prevProfId.current !== selectedProfId || prevTab.current !== activeTab) {
      setCheckedRows(new Set());
      prevProfId.current = selectedProfId;
      prevTab.current = activeTab;
    }
  }, [selectedProfId, activeTab]);

  // Sorting handlers
  const handleSortByTime = useCallback(() => {
    if (sortingMode === "time") {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortingMode("time");
      setSortDirection("desc");
    }
  }, [sortingMode, sortDirection]);

  const handleSortByMatter = useCallback(() => {
    if (sortingMode === "matter") {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortingMode("matter");
      setSortDirection("asc");
    }
  }, [sortingMode, sortDirection]);


  // Source entries based on tab (from cache, already split by exported status)
  const sourceEntries = activeTab === "exported" ? cachedExported : cachedTracked;

  // Available clients/matters for filter dropdowns (from all entries for this attorney, not date-filtered)
  const allProfEntriesForFilters = useMemo(() => [...cachedTracked, ...cachedExported], [cachedTracked, cachedExported]);

  const availableClients = useMemo(() => {
    const set = new Set<string>();
    for (const e of allProfEntriesForFilters) if (e.client) set.add(e.client);
    return Array.from(set).sort();
  }, [allProfEntriesForFilters]);

  const availableMatters = useMemo(() => {
    const map = new Map<string, { matterId: string; matterNumber: string; matterDescription: string; client: string }>();
    for (const e of allProfEntriesForFilters) {
      if (e.matter_id && !map.has(e.matter_id)) {
        if (!clientFilter || e.client === clientFilter) {
          map.set(e.matter_id, {
            matterId: e.matter_id,
            matterNumber: e.matter_number || "",
            matterDescription: e.matter_description || "",
            client: e.client || "Unknown",
          });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => a.matterNumber.localeCompare(b.matterNumber));
  }, [allProfEntriesForFilters, clientFilter]);

  // Filter by date + client + matter (client-side)
  const dateFilteredEntries = useMemo(() => {
    let entries = sourceEntries;

    // Date filtering
    if (dateRange.from || dateRange.to) {
      entries = entries.filter((e) => {
        const entryDate = new Date(e.date.split("T")[0]);
        if (dateRange.from && entryDate < dateRange.from) return false;
        if (dateRange.to && entryDate > dateRange.to) return false;
        return true;
      });
    } else {
      entries = entries.filter((e) => e.date.split("T")[0] === selectedDateStr);
    }

    // Client/matter filtering
    if (clientFilter) entries = entries.filter((e) => e.client === clientFilter);
    if (matterFilter) entries = entries.filter((e) => e.matter_id === matterFilter);

    return entries;
  }, [sourceEntries, selectedDateStr, dateRange, clientFilter, matterFilter]);

  // Sort and transform
  const tabFilteredEntries = useMemo(() => {
    const mult = sortDirection === "asc" ? 1 : -1;
    if (sortingMode === "matter") {
      return [...dateFilteredEntries].map(toEntry).sort((a, b) => {
        const mA = a.matter_name || a.matter || "";
        const mB = b.matter_name || b.matter || "";
        if (!mA && mB) return 1;
        if (mA && !mB) return -1;
        if (!mA && !mB) return 0;
        return mult * mA.toLowerCase().localeCompare(mB.toLowerCase());
      });
    }
    return [...dateFilteredEntries]
      .sort((a, b) => mult * (a.start_time || "").localeCompare(b.start_time || ""))
      .map(toEntry);
  }, [dateFilteredEntries, sortingMode, sortDirection]);

  const filteredEntries = useMemo(() => filterEntries(tabFilteredEntries), [tabFilteredEntries, filterEntries]);

  const groupedEntries = useMemo(() => {
    if (filteredEntries.length === 0) return {};
    if (sortingMode === "matter") return groupEntriesByMatter(filteredEntries, sortDirection);
    return groupEntriesByHour(filteredEntries, sortDirection);
  }, [filteredEntries, sortingMode, sortDirection]);

  // Selection logic
  const isTrackedTab = activeTab === "tracked";
  const selectionCount = checkedRows.size;

  // Action handlers
  const selectedIds = useMemo(() => Array.from(checkedRows), [checkedRows]);

  const handleExport = useCallback(async () => {
    if (selectedIds.length === 0) return;
    await exportEntries(selectedIds);
    setCheckedRows(new Set());
  }, [selectedIds, exportEntries]);

  const handleDelete = useCallback(async () => {
    if (selectedIds.length === 0) return;
    const idSet = new Set(selectedIds);

    // Optimistic: remove entries immediately
    const rollback = optimisticUpdate('reporting_entries', (prev: ReportingEntriesData) => ({
      ...prev,
      entries: prev.entries.filter((e) => !idSet.has(e.id)),
    }));
    setCheckedRows(new Set());
    setActionInProgress("delete");

    try {
      const resp = await makeReportingRequest({ endpoint: "/records/delete", method: "POST", body: { ids: selectedIds } });
      if (resp.error) throw new Error(String(resp.error));
      toast.success(`Deleted ${selectedIds.length} ${selectedIds.length === 1 ? "entry" : "entries"}`);
      refresh('reporting_entries');
    } catch (e) {
      rollback();
      refresh('reporting_entries');
      toast.error("Failed to delete entries", { duration: TOAST_DURATION.error, description: e instanceof Error ? e.message : undefined });
    } finally {
      setActionInProgress(null);
    }
  }, [selectedIds, makeReportingRequest, optimisticUpdate, refresh]);

  const handleDuplicate = useCallback(async () => {
    if (selectedIds.length === 0) return;

    // Mark source rows as mutating (no optimistic add — don't know new IDs)
    setMutatingIds((prev) => { const next = new Set(prev); selectedIds.forEach((id) => next.add(id)); return next; });
    setActionInProgress("duplicate");

    try {
      const resp = await makeReportingRequest({ endpoint: "/records/duplicate", method: "POST", body: { ids: selectedIds } });
      if (resp.error) throw new Error(String(resp.error));
      setCheckedRows(new Set());
      toast.success(`Duplicated ${selectedIds.length} ${selectedIds.length === 1 ? "entry" : "entries"}`);
      refresh('reporting_entries');
    } catch (e) {
      toast.error("Failed to duplicate entries", { duration: TOAST_DURATION.error, description: e instanceof Error ? e.message : undefined });
    } finally {
      setMutatingIds((prev) => { const next = new Set(prev); selectedIds.forEach((id) => next.delete(id)); return next; });
      setActionInProgress(null);
    }
  }, [selectedIds, makeReportingRequest, refresh]);

  const handleCombine = useCallback(() => {
    if (selectedIds.length < 2) {
      toast.error("Select at least 2 entries to combine", { duration: TOAST_DURATION.error });
      return;
    }
    setCombineDialogOpen(true);
  }, [selectedIds]);

  const handleCombineConfirm = useCallback(async (ids: number[], matterId: string, mainEntryId: number) => {
    const nonMainIds = new Set(ids.filter((id) => id !== mainEntryId));

    // Optimistic: remove non-main entries, mark main as mutating
    const rollback = optimisticUpdate('reporting_entries', (prev: ReportingEntriesData) => ({
      ...prev,
      entries: prev.entries.filter((e) => !nonMainIds.has(e.id)),
    }));
    setMutatingIds((prev) => { const next = new Set(prev); next.add(mainEntryId); return next; });
    setCheckedRows(new Set());
    setCombineDialogOpen(false);
    setActionInProgress("combine");

    try {
      const resp = await makeReportingRequest({
        endpoint: "/records/combine",
        method: "POST",
        body: { ids, matter_id: matterId, main_entry_id: mainEntryId },
      });
      if (resp.error) throw new Error(String(resp.error));
      toast.success(`${ids.length} entries combined`);
      refresh('reporting_entries');
    } catch (e) {
      rollback();
      refresh('reporting_entries');
      toast.error("Failed to combine entries", { duration: TOAST_DURATION.error, description: e instanceof Error ? e.message : undefined });
    } finally {
      setMutatingIds((prev) => { const next = new Set(prev); next.delete(mainEntryId); return next; });
      setActionInProgress(null);
    }
  }, [makeReportingRequest, optimisticUpdate, refresh]);

  const handleAddEntry = useCallback(() => {
    if (!selectedProfId) {
      toast.error("Select a team member before adding an entry");
      return;
    }
    addDialogRef.current?.openWithDuration(0);
  }, [selectedProfId]);

  const handleSaveNewEntry = useCallback(async (
    newEntry: { id: number; date: string; matter: string; matter_id: string; description: string; duration: number; task: string; activity: string },
    shouldExport: boolean
  ) => {
    try {
      const resp = await makeReportingRequest({
        endpoint: "/records/add",
        method: "POST",
        body: { ...newEntry, end_time: new Date().toLocaleTimeString() },
      });
      if (resp.error) throw new Error(String(resp.error));

      const newEntryId = resp.data?.id;

      if (shouldExport && newEntryId) {
        const exportResp = await makeReportingRequest({
          endpoint: "/records/exportRecords",
          method: "POST",
          body: { ids: [newEntryId] },
        });
        if (exportResp.error) throw new Error(String(exportResp.error));
        toast.success("Entry added and exported");
      } else {
        toast.success("Entry added");
      }

      refresh('reporting_entries');
    } catch (e) {
      if (e instanceof Error) logger.captureException(e, { action: 'teamAddEntry', shouldExport });
      toast.error("Failed to add entry", {
        duration: TOAST_DURATION.error,
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }, [makeReportingRequest, refresh]);

  const handleSaveEditEntry = useCallback(async (
    updatedEntry: { id: number; date: string; matter: string; matter_id: string; description: string; duration: number; task: string; activity: string },
    shouldExport: boolean
  ) => {
    // Optimistic: update entry fields immediately
    const selectedMatter = matters.find((m) => m.matter_id === updatedEntry.matter_id);
    const rollback = optimisticUpdate('reporting_entries', (prev: ReportingEntriesData) => ({
      ...prev,
      entries: prev.entries.map((e) => {
        if (e.id !== updatedEntry.id) return e;
        return {
          ...e,
          description: updatedEntry.description,
          matter_id: updatedEntry.matter_id,
          matter_number: selectedMatter?.matter ?? e.matter_number,
          matter_description: selectedMatter?.description ?? e.matter_description,
          client: selectedMatter?.client ?? e.client,
          units: updatedEntry.duration,
          task: updatedEntry.task,
          activity: updatedEntry.activity,
        };
      }),
    }));
    setMutatingIds((prev) => { const next = new Set(prev); next.add(updatedEntry.id); return next; });
    setShowEditModal(false);

    try {
      const resp = await makeReportingRequest({
        endpoint: "/records/edit",
        method: "POST",
        body: updatedEntry,
      });
      if (resp.error) throw new Error(String(resp.error));

      if (shouldExport) {
        const exportResp = await makeReportingRequest({
          endpoint: "/records/exportRecords",
          method: "POST",
          body: { ids: [updatedEntry.id] },
        });
        if (exportResp.error) throw new Error(String(exportResp.error));
        toast.success("Entry saved and exported");
      } else {
        toast.success("Entry saved");
      }

      refresh('reporting_entries');
    } catch (e) {
      rollback();
      refresh('reporting_entries');
      if (e instanceof Error) logger.captureException(e, { action: 'teamEditEntry', entryId: updatedEntry.id, shouldExport });
      toast.error("Failed to save entry", {
        duration: TOAST_DURATION.error,
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setMutatingIds((prev) => { const next = new Set(prev); next.delete(updatedEntry.id); return next; });
    }
  }, [makeReportingRequest, matters, optimisticUpdate, refresh]);

  const handleEditDuplicate = useCallback(async (
    entry: { date: string; matter: string; matter_id: string; description: string; duration: number; task: string; activity: string; document_name: string | null; client: string | null; units_seconds: number; start_time: string | null; end_time: string | null },
    originalId: number
  ) => {
    setMutatingIds((prev) => { const next = new Set(prev); next.add(originalId); return next; });
    try {
      const resp = await makeReportingRequest({
        endpoint: "/records/duplicate",
        method: "POST",
        body: { entry, duplicated_from_id: originalId },
      });
      if (resp.error) throw new Error(String(resp.error));
      toast.success("Entry duplicated");
      refresh('reporting_entries');
    } catch {
      toast.error("Failed to duplicate entry", { duration: TOAST_DURATION.error });
    } finally {
      setMutatingIds((prev) => { const next = new Set(prev); next.delete(originalId); return next; });
    }
  }, [makeReportingRequest, refresh]);

  // Resolve attorney feedback
  const handleResolveFeedback = useCallback(async (entryId: number) => {
    try {
      const resp = await makeReportingRequest({
        endpoint: `/records/${entryId}/resolve-feedback`,
        method: "POST",
      });
      if (resp.error) throw new Error(String(resp.error));
      refresh('reporting_entries');
      setSelectedRowData((prev: any) => prev ? { ...prev, attorney_feedback: null } : null);
      toast.success("Feedback resolved");
    } catch (e) {
      toast.error("Failed to resolve feedback", { duration: TOAST_DURATION.error });
    }
  }, [makeReportingRequest, refresh]);

  // Row click → edit (tracked tab only)
  const handleRowClick = activeTab === "tracked" ? (entry: Entry) => {
    setSelectedRowData({
      id: entry.id,
      date: entry.date,
      matter_name: entry.matter,
      matter_id: entry.matter_id || "",
      description: entry.description,
      duration: entry.duration,
      task: entry.task,
      activity: entry.activity || "",
      document_name: entry.document_name || "",
    });
    setShowEditModal(true);
  } : undefined;

  // Calendar helpers
  const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

  // Derive daily sums from cached entries (no separate API call)
  const allProfEntries = useMemo(() => [...cachedTracked, ...cachedExported], [cachedTracked, cachedExported]);

  const dailySums = useMemo(() => {
    const sums: Record<string, { unexported: number; exported: number }> = {};
    for (const tx of allProfEntries) {
      const datePart = tx.date.split("T")[0];
      const [year, month, day] = datePart.split("-").map(Number);
      const d = new Date(year, month - 1, day);
      const k = dayKey(d);
      sums[k] = sums[k] || { unexported: 0, exported: 0 };
      if (tx.exported) sums[k].exported += tx.units;
      else sums[k].unexported += tx.units;
    }
    return sums;
  }, [allProfEntries]);

  const calendarDays = useMemo(() => {
    const firstOfMonth = new Date(calendarMonthDate.getFullYear(), calendarMonthDate.getMonth(), 1);
    const startDay = new Date(firstOfMonth);
    startDay.setDate(startDay.getDate() - startDay.getDay());
    const days: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(startDay);
      d.setDate(startDay.getDate() + i);
      days.push(d);
    }
    return days;
  }, [calendarMonthDate]);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  // Empty state
  const emptyMessage = useMemo(() => {
    if (!selectedProfId) return "Select a team member to view their time entries.";
    if (filteredEntries.length > 0) return undefined;
    if (tabFilteredEntries.length > 0) return "No entries match the current filter.";
    if (sourceEntries.length > 0)
      return activeTab === "tracked" ? "No tracked entries for this date." : "No exported entries for this date.";
    return "No entries for this date.";
  }, [selectedProfId, filteredEntries.length, tabFilteredEntries.length, sourceEntries.length, activeTab]);

  if (reportsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Loading team...</p>
      </div>
    );
  }

  // Map team tab to HeaderBar viewMode
  const headerViewMode = activeTab === "exported" ? "history" as const : "entries" as const;

  // Tab pills for HeaderBar rightContent
  const tabPills = (
    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ReportingTab)}>
      <TabsList className="h-7 sm:h-8">
        <TabsTrigger value="exported" className="text-[11px] sm:text-xs px-2 sm:px-3 h-5 sm:h-6 gap-1">
          <Send className="size-3" />Exported
        </TabsTrigger>
        <TabsTrigger value="tracked" className="text-[11px] sm:text-xs px-2 sm:px-3 h-5 sm:h-6 gap-1">
          <Timer className="size-3" />Tracked
        </TabsTrigger>
        <TabsTrigger value="calendar" className="text-[11px] sm:text-xs px-2 sm:px-3 h-5 sm:h-6 gap-1">
          <Calendar className="size-3" />Calendar
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Top bar: team member selector + filters + tab pills */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-border">
        <ReportingMemberSelector
          reports={reports}
          selectedProfId={selectedProfId}
          onSelect={setSelectedProfId}
        />

        {/* Client filter */}
        <Popover>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted transition-colors max-w-[180px]">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="truncate">{clientFilter || "All Clients"}</span>
              <ChevronsUpDown className="h-3 w-3 text-muted-foreground shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[240px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search clients..." />
              <CommandList>
                <CommandEmpty>No clients found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem onSelect={() => setParams({ client: null, matter: null })}>
                    <Check className={cn("mr-2 h-4 w-4", !clientFilter ? "opacity-100" : "opacity-0")} />
                    All Clients
                  </CommandItem>
                  {availableClients.map((c) => (
                    <CommandItem key={c} value={c} onSelect={() => setParams({ client: c, matter: null })}>
                      <Check className={cn("mr-2 h-4 w-4", clientFilter === c ? "opacity-100" : "opacity-0")} />
                      {c}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Matter filter */}
        <Popover>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted transition-colors max-w-[200px]">
              <FolderOpen className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="truncate">
                {matterFilter ? (availableMatters.find((m) => m.matterId === matterFilter)?.matterNumber || "Matter") : "All Matters"}
              </span>
              <ChevronsUpDown className="h-3 w-3 text-muted-foreground shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search matters..." />
              <CommandList>
                <CommandEmpty>No matters found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem onSelect={() => setParams({ matter: null })}>
                    <Check className={cn("mr-2 h-4 w-4", !matterFilter ? "opacity-100" : "opacity-0")} />
                    All Matters
                  </CommandItem>
                  {availableMatters.map((m) => (
                    <CommandItem
                      key={m.matterId}
                      value={`${m.matterNumber} ${m.matterDescription}`}
                      onSelect={() => setParams({ matter: m.matterId, client: m.client })}
                    >
                      <Check className={cn("mr-2 h-4 w-4 shrink-0", matterFilter === m.matterId ? "opacity-100" : "opacity-0")} />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate text-sm">{m.matterNumber}</span>
                        <span className="text-xs text-muted-foreground truncate">{m.matterDescription}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Clear filters */}
        {(clientFilter || matterFilter) && (
          <button
            className="flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            onClick={() => setParams({ client: null, matter: null })}
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        )}

        <div className="ml-auto">
          {tabPills}
        </div>
      </div>

      {/* HeaderBar — action buttons, date picker, export (tracked/exported tabs only) */}
      {activeTab !== "calendar" && (
        <div className="px-3">
        <HeaderBar
          viewMode={headerViewMode}
          date={selectedDate}
          onDateChange={setSelectedDate}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          showTimer={false}
          timerRunning={false}
          elapsedSeconds={0}
          onStart={() => {}}
          onStop={() => {}}
          onReset={() => {}}
          selectionCount={selectionCount}
          onAddEntry={handleAddEntry}
          onCombine={handleCombine}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          onExport={handleExport}
          isExporting={isExporting}
          actionInProgress={actionInProgress}
          isApplicationVisible={isApplicationVisible}
          onToggleApplication={toggleApplication}
          isFilterActive={isFilterActive}
          onShowAllApplications={showAllApplications}
          onHideAllApplications={hideAllApplications}
        />
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {activeTab === "calendar" ? (
          <div className="border border-border rounded-lg overflow-hidden flex-1 flex flex-col mx-3 mb-3 mt-4">
            {!selectedProfId ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                Select a team member to view their calendar
              </div>
            ) : entriesLoading ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">Loading...</div>
            ) : (
              <div className={calendarStyles.calendarView}>
                <div className={calendarStyles.calendarHeader}>
                  <span className={calendarStyles.calendarHeaderLabel}>
                    {calendarMonthDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                  </span>
                  <div className={calendarStyles.calendarHeaderButtonWrapper}>
                    <button className={calendarStyles.calendarPageButton} onClick={() => { const d = new Date(calendarMonthDate); d.setMonth(d.getMonth() - 1); setCalendarMonthDate(d); }}>
                      <img src={darkMode ? navArrowLeftWhiteIcon : navArrowLeftIcon} alt="Previous" />
                    </button>
                    <button className={`${calendarStyles.calendarPageButton} ${calendarStyles.calendarTodayButton}`} onClick={() => setCalendarMonthDate(new Date())}>
                      Today
                    </button>
                    <button className={calendarStyles.calendarPageButton} onClick={() => { const d = new Date(calendarMonthDate); d.setMonth(d.getMonth() + 1); setCalendarMonthDate(d); }}>
                      <img src={darkMode ? navArrowRightWhiteIcon : navArrowRightIcon} alt="Next" />
                    </button>
                  </div>
                </div>
                <div className={calendarStyles.calendarGrid}>
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div key={day} className={calendarStyles.calendarWeekdayHeader}>{day}</div>
                  ))}
                  {calendarDays.map((currDay, index) => {
                    const isCurrentMonth = currDay.getMonth() === calendarMonthDate.getMonth();
                    const isWeekend = currDay.getDay() === 0 || currDay.getDay() === 6;
                    const key = dayKey(currDay);
                    const unexported = (dailySums[key]?.unexported || 0).toFixed(1);
                    const exported = (dailySums[key]?.exported || 0).toFixed(1);
                    return (
                      <button key={index} onClick={() => { if (selectedProfId) { setSelectedDate(currDay); setActiveTab("tracked"); } }}>
                        <div
                          className={`${calendarStyles.calendarCard} ${!isCurrentMonth ? calendarStyles.calendarOutsideMonthCard : ""} ${isWeekend ? calendarStyles.calendarWeekendCard : ""}`}
                          data-today={isSameDay(currDay, new Date())}
                        >
                          <div className={calendarStyles.cardDate}>{currDay.getDate()}</div>
                          <div className={`${calendarStyles.cardMetric} ${calendarStyles.cardMetricUnits}`}>{unexported} hrs to export</div>
                          <div className={`${calendarStyles.cardMetric} ${calendarStyles.cardMetricUnits}`}>{exported} hrs exported</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="border border-border rounded-lg overflow-hidden flex-1 flex flex-col mx-3 mb-3">
              {entriesLoading ? (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">Loading...</div>
              ) : isTrackedTab ? (
                <ReportingTrackedTable
                  key={dateRange ? `${dateRange.from?.toDateString()}-${dateRange.to?.toDateString()}` : selectedDate.toDateString()}
                  entries={filteredEntries}
                  groupedEntries={groupedEntries}
                  sortingMode={sortingMode}
                  sortDirection={sortDirection}
                  onSortByTime={handleSortByTime}
                  onSortByMatter={handleSortByMatter}
                  onRowClick={selectedProfId ? handleRowClick : undefined}
                  exportingEntryIds={exportingIds}
                  mutatingIds={mutatingIds}
                  failedExportEntries={failedExportEntries}
                  matters={matters}
                  getRestrictedWords={getRestrictedWords}
                  emptyMessage={emptyMessage}
                  checkedRows={checkedRows}
                  onCheckedRowsChange={setCheckedRows}
                  autoScrollToBottom={!dateRange && selectedDate.toDateString() === new Date().toDateString()}
                />
              ) : (
                <ReportingExportedTable
                  key={dateRange ? `${dateRange.from?.toDateString()}-${dateRange.to?.toDateString()}` : selectedDate.toDateString()}
                  entries={filteredEntries}
                  groupedEntries={groupedEntries}
                  sortingMode={sortingMode}
                  sortDirection={sortDirection}
                  onSortByTime={handleSortByTime}
                  onSortByMatter={handleSortByMatter}
                  emptyMessage={emptyMessage}
                  exportingEntryIds={exportingIds}
                  mutatingIds={mutatingIds}
                  matters={matters}
                  getRestrictedWords={getRestrictedWords}
                />
              )}
            </div>

          </>
        )}
      </div>

      {/* Add Entry Dialog */}
      <AddEntryV2
        ref={addDialogRef}
        onSave={handleSaveNewEntry}
        matters={matters}
        tasks={tasks}
        activities={activities}
        currentDate={selectedDate}
        abbreviations={abbreviationProps}
      />

      {/* Edit Entry Dialog */}
      {selectedRowData && (
        <EditEntryV2
          onSave={handleSaveEditEntry}
          onDuplicate={handleEditDuplicate}
          onResolveFeedback={() => handleResolveFeedback(selectedRowData.id)}
          matters={matters}
          tasks={tasks}
          activities={activities}
          selectedRowData={selectedRowData}
          open={showEditModal}
          onOpenChange={setShowEditModal}
          abbreviations={abbreviationProps}
        />
      )}

      {/* Combine Dialog */}
      <CombineDialog
        open={combineDialogOpen}
        onOpenChange={setCombineDialogOpen}
        entries={filteredEntries.filter((e) => checkedRows.has(e.id))}
        matters={matters}
        onConfirm={handleCombineConfirm}
        onCancel={() => setCombineDialogOpen(false)}
      />
    </div>
  );
}
